import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Auswahl, Eingabe, Schalter } from "@kundeninfo/ui/me-design";
import type { EingabeProps, KnopfProps } from "@kundeninfo/ui/me-design";
import { zahlAus, mitEinheit } from "../layoutEntwurf.js";
import { KiKnopf, useBeimVerlassen } from "../../meHuellen.js";

/*
 * Die FELDER der Register (ME-Etappe 4, P3): Hüllen über `me-eingabe`, `me-auswahl`,
 * `me-schalter` und `me-knopf` statt eigener `<input>`/`<select>`/`<button>`. Seit dem
 * Tarball a59b8aa (P4b) bringen die Elemente selbst mit, was hier bis dahin nachgebaut
 * stand: die Einheit im Feld (`einheit`, Entscheid 39), den Systemwert als `platzhalter`,
 * die Ansage eines Befunds (`aria-live` am Fehlertext) und die Sperre über `fieldset
 * disabled` (formDisabledCallback: gesperrt, der Fokus bleibt). Kundeninfo-Zutat bleibt:
 *
 *  - VERLASSEN: die Hülle reicht kein `onBlur` durch; `useBeimVerlassen` hört am Host auf
 *    `blur` (das innere Feld meldet es über die Schattengrenze an den Host).
 *  - „SYSTEMWERT“ im Zustand „Fremder Stand“: Felder UND Knöpfe sperrt die `fieldset
 *    disabled` des Editors (me-knopf ist seit me-design 6bfec97 formularassoziiert, Befund
 *    P4b-1 gelöst). `PanelSperre` sagt den Feldern nur noch, dass sie die Zurücknahme auf
 *    den Systemwert gar nicht erst ANBIETEN — ein abgeschalteter Knopf neben jedem eigenen
 *    Wert wäre Lärm.
 */

/** Das Panel ist gesperrt (Fremder Stand, 409) — gesetzt vom Editor. Abgeschaltet werden
 *  Felder und Knöpfe von `fieldset disabled`; dieser Kontext blendet nur „Systemwert“ aus. */
export const PanelSperre = createContext(false);

/** Ist das Panel gesperrt (Fremder Stand)? */
export function usePanelSperre(): boolean {
  return useContext(PanelSperre);
}

/** Die DOM-Kennung zu einem Entwurfspfad — `seitenraender.oben` → `lay-seitenraender-oben`. */
export function feldId(pfad: string): string {
  return `lay-${pfad.replace(/[^A-Za-z0-9]+/g, "-")}`;
}


/** Mehrere Befunde eines Feldes zu einem Text für `fehler` (leer = keiner). */
export function befundText(...texte: (string | undefined)[]): string | undefined {
  const da = texte.filter((t): t is string => t !== undefined && t !== "");
  return da.length === 0 ? undefined : da.join(" ");
}

/**
 * Ein Eingabefeld (`me-eingabe`) der Register, kontrolliert über `wert`. `beschriftung`
 * zeigt das Element selbst; ohne sie trägt `name` den zugänglichen Namen (aria-label, für
 * Felder mit eigenem `<label for>` oder ohne sichtbare Beschriftung).
 */
export function LayEingabe(p: {
  id: string; wert: string; onText: (text: string) => void; onVerlassen?: (() => void) | undefined;
  name?: string | undefined; beschriftung?: string | undefined;
  platzhalter?: string | undefined; fehler?: string | undefined; hilfe?: string | undefined;
  max?: number | undefined; mehrzeilig?: boolean | undefined; gesperrt?: boolean | undefined;
  eigen?: boolean | undefined; schmal?: boolean | undefined; zahl?: boolean | undefined;
  einheit?: string | undefined; className?: string | undefined;
}): ReactElement {
  const host = useRef<HTMLElement | null>(null);
  const gesperrt = p.gesperrt === true;
  useBeimVerlassen(host, p.onVerlassen);
  // `exactOptionalPropertyTypes`: nur gesetzte Angaben weiterreichen.
  const optional: Partial<EingabeProps> = {};
  if (p.name !== undefined) optional["aria-label"] = p.name;
  if (p.beschriftung !== undefined) optional.beschriftung = p.beschriftung;
  if (p.fehler !== undefined && p.fehler !== "") optional.fehler = p.fehler;
  if (p.hilfe !== undefined) optional.hilfe = p.hilfe;
  if (p.platzhalter !== undefined && p.platzhalter !== "") optional.platzhalter = p.platzhalter;
  if (p.einheit !== undefined) optional.einheit = p.einheit;
  if (p.max !== undefined) optional.max = p.max;
  if (p.mehrzeilig === true) { optional.mehrzeilig = true; optional.zeilen = 3; }
  if (gesperrt) optional.gesperrt = true;
  if (p.zahl === true) optional.art = "dezimal";
  // Ohne eigene Beschriftung (nur aria-label) blendet `lay__me--ohne-beschriftung` die leere
  // Beschriftungszeile des Elements aus — sonst stünde das Feld um deren Abstand tiefer als
  // der Knopf daneben.
  const klassen = ["lay__me", p.beschriftung === undefined ? "lay__me--ohne-beschriftung" : "",
    p.eigen === true ? "lay__eigen" : "",
    p.schmal === true ? "lay__me--schmal" : "", p.className ?? ""].filter((k) => k !== "");
  return (
    <Eingabe ref={host} id={p.id} className={klassen.join(" ")} wert={p.wert} {...optional}
      onGeaendert={(d) => { if (!gesperrt) p.onText(d.wert); }} />
  );
}

/**
 * Ein Knopf der Register (`me-knopf` über `KiKnopf`). `name` ist der zugängliche Name, wo der
 * sichtbare Text allein nicht reicht („Oberer Rand: Systemwert“, „Bezeichnung nach oben“ an
 * „▲“) — er geht als `aria-label` an den Host. Im Fremden Stand schaltet ihn die `fieldset
 * disabled` des Panels ab (me-design 6bfec97); ausserhalb des Panels (Werkzeugleiste,
 * Meldungen) bleibt er bedienbar.
 */
export function LayKnopf(p: {
  art?: KnopfProps["art"]; deaktiviert?: boolean | undefined; className?: string | undefined;
  name?: string | undefined;
  onClick: () => void; children: ReactNode;
}): ReactElement {
  return (
    <KiKnopf art={p.art ?? "sekundaer"} deaktiviert={p.deaktiviert === true} className={p.className} name={p.name}
      onClick={p.onClick}>{p.children}</KiKnopf>
  );
}

/** Der Knopf „Systemwert“ eines Feldes: Name „<Feld>: Systemwert“, sichtbar „Systemwert“
 *  (mit `zusatz`, z. B. „(an)“, nur fürs Auge). */
export function SystemwertKnopf(p: {
  label: string; zusatz?: string | undefined; onClick: () => void;
}): ReactElement {
  return (
    <LayKnopf art="leise" className="lay__systemwert" name={`${p.label}: Systemwert`}
      onClick={p.onClick}>
      Systemwert{p.zusatz !== undefined && ` ${p.zusatz}`}
    </LayKnopf>
  );
}

/**
 * Ein Zahlenfeld mit Einheit (mm, pt, %). Die Einheit steht gedämpft rechts IM Feld und wird
 * mitgelesen (`einheit`, Entscheid 39); der SYSTEMWERT steht als `platzhalter`. Ein eigener
 * Wert ist hervorgehoben und lässt sich mit „Systemwert" zurücknehmen. Die Eingabe bleibt
 * Text, solange getippt wird (auch „18," ist ein Zwischenstand), und geht bei jeder Änderung
 * als „18.5mm" in den Entwurf. Die Beschriftung steht als eigenes `<label for>` über der Zeile
 * [Feld | Systemwert], damit der Knopf auf Höhe des Feldes steht.
 */
export function EinheitFeld(p: {
  id: string; label: string; einheit: "mm" | "pt" | "%"; wert: unknown; systemwert: string;
  fehler: string | undefined; onWert: (neu: string | undefined) => void;
  onVerlassen: () => void;
}): ReactElement {
  const eigen = p.wert !== undefined;
  const gesperrt = usePanelSperre();
  const [text, setText] = useState(zahlAus(p.wert, p.einheit));
  // Kommt der Wert von aussen (Verwerfen, Übernehmen, Systemwert), folgt das Feld.
  useEffect(() => {
    setText((alt) => (mitEinheit(alt, p.einheit) === p.wert ? alt : zahlAus(p.wert, p.einheit)));
  }, [p.wert, p.einheit]);
  return (
    <div className="lay__feld">
      <label className="lay__label" htmlFor={p.id}>{p.label}</label>
      <div className="lay__eingabezeile">
        <LayEingabe id={p.id} name={p.label} wert={text} zahl schmal eigen={eigen}
          einheit={p.einheit} platzhalter={zahlAus(p.systemwert, p.einheit)} fehler={p.fehler}
          onText={(t) => { setText(t); p.onWert(mitEinheit(t, p.einheit)); }}
          onVerlassen={p.onVerlassen} />
        {eigen && !gesperrt && (
          <SystemwertKnopf label={p.label}
            onClick={() => { setText(""); p.onWert(undefined); p.onVerlassen(); }} />
        )}
      </div>
    </div>
  );
}

/** Text → Zahl für ZahlFeld: leer = undefined, Komma wird Punkt; was keine Zahl ist, geht
 *  als Text hinaus — der Server sagt am Feld, was erlaubt ist (wie EinheitFeld). */
export function zahlEingabe(text: string): number | string | undefined {
  const roh = text.trim().replace(",", ".");
  if (roh === "") return undefined;
  const n = Number(roh);
  return Number.isFinite(n) ? n : text.trim();
}

/**
 * Ein Zahlenfeld für Entwurfswerte, die als ZAHL im Layout stehen (Belegdesign Etappe 2:
 * `logo.prozent`, `logo.xMm`, `logo.yMm`) — dieselbe Familie wie EinheitFeld (Platzhalter,
 * Fehler am Feld, Kennung aus dem Pfad für den Sprung zum ersten Fehler), nur ohne Einheit
 * im Wert. `hinweis` steht gedämpft unter dem Feld (z. B. Warnung 68 a). `systemwertKnopf`
 * nimmt einen eigenen Wert zurück.
 */
export function ZahlFeld(p: {
  id: string; label: string; einheit: "mm" | "%"; wert: unknown; platzhalter: string;
  fehler: string | undefined; hinweis?: string | undefined; gesperrt?: boolean | undefined;
  systemwertKnopf?: boolean | undefined;
  onWert: (neu: number | string | undefined) => void; onVerlassen: () => void;
}): ReactElement {
  const eigen = p.wert !== undefined;
  const alsText = (w: unknown): string =>
    typeof w === "number" || typeof w === "string" ? String(w) : "";
  const [text, setText] = useState(alsText(p.wert));
  // Kommt der Wert von aussen (Ziehen im Blatt, Verwerfen, „Wie bisher“), folgt das Feld;
  // ein Zwischenstand wie „18,“ bleibt stehen, solange er denselben Wert meint.
  useEffect(() => {
    setText((alt) => (zahlEingabe(alt) === p.wert ? alt : alsText(p.wert)));
  }, [p.wert]);
  const gesperrt = usePanelSperre() || p.gesperrt === true;
  return (
    <div className="lay__feld lay__feld--klein">
      <label className="lay__label" htmlFor={p.id}>{p.label}</label>
      <div className="lay__eingabezeile">
        <LayEingabe id={p.id} name={p.label} wert={text} zahl schmal eigen={eigen}
          einheit={p.einheit} platzhalter={p.platzhalter} fehler={p.fehler} hilfe={p.hinweis}
          gesperrt={p.gesperrt}
          onText={(t) => { setText(t); p.onWert(zahlEingabe(t)); }}
          onVerlassen={p.onVerlassen} />
        {eigen && p.systemwertKnopf === true && !gesperrt && (
          <SystemwertKnopf label={p.label}
            onClick={() => { setText(""); p.onWert(undefined); p.onVerlassen(); }} />
        )}
      </div>
    </div>
  );
}

/**
 * Ein FARBFELD (`me-eingabe art="farbe"`, Vorbild Akzentfarbe im Register „Marke“): leer heisst Systemwert — der
 * steht als Platzhalter (z. B. „Akzentfarbe“) —, ein eigener Wert ist `#rrggbb` und lässt sich zurücknehmen. Eine
 * fertige Farbe (Palette, Systemwähler, sechs Stellen) fordert die Vorschau gleich an, sonst das Verlassen.
 */
export function FarbFeld(p: {
  id: string; label: string; wert: unknown; systemwert: string;
  palette: readonly { hex: string; name: string }[]; fehler: string | undefined;
  onWert: (neu: string | undefined) => void; onVerlassen: () => void;
}): ReactElement {
  const gesperrt = usePanelSperre();
  const [text, setText] = useState(typeof p.wert === "string" ? p.wert : "");
  useEffect(() => {
    setText((alt) => (alt === p.wert ? alt : typeof p.wert === "string" ? p.wert : ""));
  }, [p.wert]);
  const host = useRef<HTMLElement | null>(null);
  useBeimVerlassen(host, p.onVerlassen);
  const eigen = p.wert !== undefined;
  return (
    <div className="lay__feld">
      <div className="lay__eingabezeile">
        <Eingabe ref={host} id={p.id} art="farbe" beschriftung={p.label} wert={text} platzhalter={p.systemwert}
          className={`lay__me${eigen ? " lay__eigen" : ""}`}
          {...(p.fehler === undefined ? {} : { fehler: p.fehler })}
          onGeaendert={(d) => {
            const neu = d.wert.trim();
            setText(d.wert);
            p.onWert(neu === "" ? undefined : neu);
            if (/^#[0-9A-Fa-f]{6}$/.test(neu)) p.onVerlassen();
          }}>
          {p.palette.map((f) => <option key={f.hex} value={f.hex}>{f.name}</option>)}
        </Eingabe>
        {eigen && !gesperrt && (
          <SystemwertKnopf label={p.label}
            onClick={() => { setText(""); p.onWert(undefined); p.onVerlassen(); }} />
        )}
      </div>
    </div>
  );
}

/** Eine Auswahl, deren leerer Wert „Systemwert" heisst. Eine Wahl ist eine fertige

 *  Eingabe — der Aufrufer fordert die Vorschau gleich an. */
export function WahlFeld(p: {
  id: string; label: string; wert: string; optionen: readonly { wert: string; text: string }[];
  fehler: string | undefined; onWahl: (wert: string) => void;
}): ReactElement {
  return (
    <div className="lay__feld">
      <Auswahl id={p.id} beschriftung={p.label} wert={p.wert}
        className={`lay__me${p.wert !== "" ? " lay__eigen" : ""}`}
        {...(p.fehler === undefined ? {} : { fehler: p.fehler })}
        onGeaendert={(d) => p.onWahl(d.wert)}>
        {p.optionen.map((o) => <option key={o.wert} value={o.wert}>{o.text}</option>)}
      </Auswahl>
    </div>
  );
}

/** Ein Schalter: zeigt den WIRKSAMEN Wert (eigen, sonst System); ein eigener Wert ist
 *  markiert und lässt sich zurücknehmen. */
export function SchalterFeld(p: {
  id: string; label: string; wert: unknown; systemwert: boolean; fehler: string | undefined;
  onWert: (neu: boolean | undefined) => void;
}): ReactElement {
  const eigen = typeof p.wert === "boolean";
  const an = eigen ? p.wert as boolean : p.systemwert;
  const gesperrt = usePanelSperre();
  return (
    <div className="lay__feld">
      <div className="lay__eingabezeile">
        <Schalter id={p.id} beschriftung={p.label} an={an}
          className={`lay__me${eigen ? " lay__eigen" : ""}`}
          {...(p.fehler === undefined ? {} : { fehler: p.fehler })}
          onGeaendert={(d) => p.onWert(d.an)} />
        {eigen && !gesperrt && (
          <SystemwertKnopf label={p.label} zusatz={`(${p.systemwert ? "an" : "aus"})`}
            onClick={() => p.onWert(undefined)} />
        )}
      </div>
    </div>
  );
}

/** Der Wortlaut der Markdown-Regel und des Sofort-Hinweises (Fixrunde 1, W1). */
const MARKDOWN_HILFE = "Markdown im Beleg-Tagsatz; kein HTML — die Zeichen < und > sind nicht erlaubt";
const SPITZE_KLAMMERN = "Die Zeichen < und > sind nicht erlaubt.";

/** Ein Text (einzeilig oder mehrzeilig). Leer heisst „Systemwert"; der steht als
 *  Platzhalter. `hinweis` sagt, woher ein Platzhalter stammt (Erbe der Korrektur).
 *
 *  Fixrunde 1 (W1), beides optional: `max` zeigt einen Zähler „n / max" (das Element warnt
 *  ab 95 %, darüber Fehlerfarbe); `markdown` nennt die Regel des Beleg-Tagsatzes unter dem
 *  Feld und weist `<`/`>` sofort am Feld ab, noch vor dem Rundlauf — maßgeblich bleibt der
 *  Server. */
export function TextFeld(p: {
  id: string; label: string; wert: unknown; systemwert: string; mehrzeilig: boolean;
  hinweis?: string | undefined; fehler: string | undefined;
  max?: number | undefined; markdown?: boolean | undefined;
  /** Falsch: kein Knopf „Systemwert“ (z. B. Fußspalten: leer ist keine Systemvorgabe). */
  systemwertKnopf?: boolean | undefined;
  /** Zugänglicher Name, wenn er länger ist als die sichtbare Beschriftung (Fußspalten: sichtbar „Fußspalte 1: Text“
   *  unter der Zwischenüberschrift, zugänglich „Seite 1, Fußspalte 1: Text“ — eindeutig im Register). */
  name?: string | undefined;
  onWert: (neu: string | undefined) => void; onVerlassen: () => void;
}): ReactElement {
  const eigen = typeof p.wert === "string";
  const text = eigen ? p.wert as string : "";
  const gesperrt = usePanelSperre();
  const klammern = p.markdown === true && /[<>]/.test(text);
  return (
    <div className="lay__feld">
      <LayEingabe id={p.id} beschriftung={p.label} name={p.name} wert={text} eigen={eigen}
        mehrzeilig={p.mehrzeilig} platzhalter={p.systemwert} max={p.max}
        hilfe={p.markdown === true ? MARKDOWN_HILFE : undefined}
        fehler={befundText(klammern ? SPITZE_KLAMMERN : undefined, p.fehler)}
        onText={(t) => p.onWert(t === "" ? undefined : t)} onVerlassen={p.onVerlassen} />
      {p.hinweis !== undefined && !eigen && <span className="lay__erbe">{p.hinweis}</span>}
      {eigen && p.systemwertKnopf !== false && !gesperrt && (
        <div className="lay__werkzeuge">
          <SystemwertKnopf label={p.name ?? p.label} onClick={() => { p.onWert(undefined); p.onVerlassen(); }} />
        </div>
      )}
    </div>
  );
}
